import { toast } from 'sonner'
import { ApiClientError } from '@/lib/api/client'
import { logRefusal, refusalKey } from '@/lib/api/refusal-key'

/**
 * The refusals a recipient write can meet, by the code RecipientService sends
 * (`code` on most bodies, `errorCode` on the capacity gate; ApiClientError
 * reads either as errorCode). A retry cannot fix any of them, so each says
 * what to change. Any other refusal reads the sentence for its status
 * (refusalKey). app/recipients/[id]/__tests__/refused-edit-says-why.spec.tsx
 * checks that RecipientService still sends every RECIPIENT_* code here.
 */
export const RECIPIENT_REFUSAL_KEYS: ReadonlyMap<string, string> = new Map([
  // update() / create(): another live recipient of this workspace holds the address / UPN (409).
  ['RECIPIENT_EMAIL_EXISTS', 'recipients.profile.refusals.emailExists'],
  ['RECIPIENT_UPN_EXISTS', 'recipients.profile.refusals.upnExists'],
  // assertManagerAndDomainBelongToWorkspace / assertNoManagerCycle / bulkUpdateField (400).
  ['RECIPIENT_MANAGER_SELF', 'recipients.profile.refusals.managerSelf'],
  ['RECIPIENT_MANAGER_LOOP', 'recipients.profile.refusals.managerLoop'],
  ['RECIPIENT_MANAGER_CHAIN_LOOP', 'recipients.profile.refusals.managerChainLoop'],
  ['RECIPIENT_MANAGER_DELETED', 'recipients.profile.refusals.managerDeleted'],
  ['RECIPIENT_MANAGER_NOT_IN_WORKSPACE', 'recipients.profile.refusals.managerNotInWorkspace'],
  // assertRecipientCapacity: the company is at its recipient limit (400).
  ['recipient_limit_reached', 'recipients.pendingActions.resolve.recipientLimitReached'],
])

/**
 * #791 — the manager refusals once more, for when the API names who caused
 * them (`person` on the body): which selected person would loop, or which
 * chosen manager. A bulk Set Manager over many people used to say only "this
 * person". Without a name they read as above.
 */
const NAMED_REFUSAL_KEYS: ReadonlyMap<string, string> = new Map([
  ['RECIPIENT_MANAGER_SELF', 'recipients.profile.refusals.managerSelfNamed'],
  ['RECIPIENT_MANAGER_LOOP', 'recipients.profile.refusals.managerLoopNamed'],
  ['RECIPIENT_MANAGER_CHAIN_LOOP', 'recipients.profile.refusals.managerChainLoopNamed'],
])

/** The named sentence for a refusal that names a person, else null. */
function namedRefusal(error: unknown): { key: string; name: string } | null {
  if (!(error instanceof ApiClientError) || !error.errorCode) return null
  const key = NAMED_REFUSAL_KEYS.get(error.errorCode)
  const person = (error.body as { person?: { name?: unknown } } | null | undefined)?.person
  const name = typeof person?.name === 'string' ? person.name.trim() : ''
  return key && name ? { key, name } : null
}

type Translate = (key: string, params?: Record<string, string | number>) => string

/**
 * Say a refused recipient write ONCE: what failed (`titleKey`) and why (the
 * sentence for the API's code, else for its status). What the API said goes to
 * the console (logRefusal), never on screen. The recipients mutation hooks call
 * this, so the screens that use them put their values back and add no toast of
 * their own.
 */
export function toastRecipientRefusal(t: Translate, what: string, titleKey: string, error: unknown): void {
  logRefusal(what, error)
  const named = namedRefusal(error)
  toast.error(t(titleKey), {
    description: named ? t(named.key, { name: named.name }) : t(refusalKey(error, RECIPIENT_REFUSAL_KEYS)),
  })
}
